import { setRequest } from '@sancare/typescript-commons/src/misc/api'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'

import { formatRhs, formatSmrStay } from '@/misc/stay/smr/formatter'
import { CodingSelection } from '@/stay-displayer/types'
import { RawCodedMedicalAct } from '@/store/modules/medical-act/types'
import {
  Dependencies,
  RawRhs,
  RawSmrStay,
  RhsRefreshedGroupingResult,
  SmrDiagnosisGroup,
  SmrStayState
} from '@/store/modules/stay/smr/types'

export const mutations = {
  fetchStaySuccess(state: SmrStayState, stay: RawSmrStay) {
    state.rawStay = stay
    state.currentStay = formatSmrStay(stay)
  },
  fetchRhsSuccess(state: SmrStayState, responseRhs: RawRhs) {
    let rhsIdx = state.rawStay.rhsList.findIndex((rhs) => rhs.id === responseRhs.id)
    if (rhsIdx > -1) {
      state.rawStay.rhsList[rhsIdx] = responseRhs
    }
    rhsIdx = state.rawStay.rhsList.findIndex((rhs) => rhs.id === responseRhs.id)
    if (rhsIdx > -1) {
      state.currentStay.rhsList[rhsIdx] = formatRhs(responseRhs)
    }
    state.rawRhs = responseRhs
    state.currentRhs = formatRhs(responseRhs)
  },
  setCurrentRhs(state: SmrStayState, rhsId: number) {
    const rawRhs = state.rawStay.rhsList.find((rhs) => rhs.id === rhsId)
    if (!rawRhs) {
      return
    }
    state.rawRhs = rawRhs
    state.currentRhs = formatRhs(rawRhs)
  },
  startRefreshingGroupingResult(state: SmrStayState) {
    state.isGroupingResultRefreshing = true
  },
  endRefreshingGroupingResult(state: SmrStayState) {
    state.isGroupingResultRefreshing = false
  },
  setCurrentGroupingResult(state: SmrStayState, lightRhs: RhsRefreshedGroupingResult) {
    state.currentStay.standardModeGroupingResult = lightRhs.stay.standardModeGroupingResult
    this.commit('smrStay/endRefreshingGroupingResult', lightRhs)
  },
  setMainContent(state: SmrStayState, mainContent: HealthDataDocument | null) {
    state.mainContent = mainContent
  },
  setJustificationSelection(state: SmrStayState, codingSelection: CodingSelection | null) {
    state.justificationSelection = codingSelection
  },
  setScrollTarget(state: SmrStayState, scrollTarget: { docId: number, offset: number } | null) {
    state.scrollTarget = scrollTarget
  },
  // todo create TextProcessingStay type
  setCurrentStay(state: SmrStayState, withTextProcessingStay: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    state.currentStay = formatSmrStay(withTextProcessingStay)
  },
  updateStayStatusSuccess(state: SmrStayState, stay: RawSmrStay) {
    refreshStateStay(state, stay)
    this.commit('setStayListInitialized', false)
  },
  updateMedicalActsSuccess(state: SmrStayState, lightRhs: RhsRefreshedGroupingResult & { codedMedicalActs: RawCodedMedicalAct[] }) {
    const stayRhs = state.rawStay.rhsList.find((rhs) => rhs.id === lightRhs.id)
    stayRhs.codedMedicalActs = [...lightRhs.codedMedicalActs]
    this.commit('smrStay/setCurrentGroupingResult', lightRhs)
    this.commit('smrStay/setCurrentRhs', lightRhs.id)
    this.commit('setStayListInitialized', false)
  },
  updateDependenciesSuccess(state: SmrStayState, lightRhs: RhsRefreshedGroupingResult & Dependencies) {
    const rawStayRhs = state.rawStay.rhsList.find((rhs) => rhs.id === lightRhs.id)
    rawStayRhs.currentDepBehaviour = lightRhs.currentDepBehaviour
    rawStayRhs.currentDepContinence = lightRhs.currentDepContinence
    rawStayRhs.currentDepDressing = lightRhs.currentDepDressing
    rawStayRhs.currentDepFeeding = lightRhs.currentDepFeeding
    rawStayRhs.currentDepMoving = lightRhs.currentDepMoving
    rawStayRhs.currentDepSocial = lightRhs.currentDepSocial
    const currentStayRhs = state.currentStay.rhsList.find((rhs) => rhs.id === lightRhs.id)
    currentStayRhs.currentDepBehaviour = lightRhs.currentDepBehaviour
    currentStayRhs.currentDepContinence = lightRhs.currentDepContinence
    currentStayRhs.currentDepDressing = lightRhs.currentDepDressing
    currentStayRhs.currentDepFeeding = lightRhs.currentDepFeeding
    currentStayRhs.currentDepMoving = lightRhs.currentDepMoving
    currentStayRhs.currentDepSocial = lightRhs.currentDepSocial
    this.commit('smrStay/setCurrentGroupingResult', lightRhs)
    this.commit('smrStay/setCurrentRhs', lightRhs.id)
    this.commit('setStayListInitialized', false)
  },
  updateSmrDiagnosisSuccess(state: SmrStayState, lightRhs: RhsRefreshedGroupingResult & { currentModeDiagnosisGroup: SmrDiagnosisGroup }) {
    const stayRhs = state.rawStay.rhsList.find((rhs) => rhs.id === lightRhs.id)
    stayRhs.currentModeDiagnosisGroup = lightRhs.currentModeDiagnosisGroup
    this.commit('smrStay/setCurrentGroupingResult', lightRhs)
    this.commit('smrStay/setCurrentRhs', lightRhs.id)
    this.commit('setStayListInitialized', false)
  },
  setRequest,
  commentStayStart(state) {
    state.commentStayRequest = { fetching: true, error: null, ok: false }
  },
  commentStaySuccess(state, stay) {
    state.commentStayRequest = { fetching: false, ok: true }
    state.currentStay.comment = stay.comment
    this.commit('setStayListInitialized', false)
  },
  commentStayError(state, res) {
    state.commentStayRequest.fetching = false
    state.commentStayRequest.error = res
  },
  createFalseJustificationsSuccess(state: SmrStayState, falseJustification) {
    const reportId = falseJustification.report ? falseJustification.report.id : null
    const healthEntryId = falseJustification.textualHealthEntry ? falseJustification.textualHealthEntry.id : null

    state.currentStay.falseJustifications = [
      ...(state.currentStay.falseJustifications ?? []),
      {
        id: falseJustification.id,
        type: falseJustification.type,
        hintStart: falseJustification.hintStart,
        hintEnd: falseJustification.hintEnd,
        documentType: reportId ? 'report' : 'textualHealthEntry',
        documentId: reportId ?? healthEntryId,
      },
    ]
  },
  deleteFalseJustificationsSuccess(state: SmrStayState, { justificationId }) {
    state.currentStay.falseJustifications = (state.currentStay.falseJustifications ?? []).filter((j) => j.id !== justificationId)
  },
}

function refreshStateStay(state: SmrStayState, stay: RawSmrStay) {
  state.rawStay = stay
  state.currentStay = formatSmrStay(stay)
}
